import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CheckCircle2, ExternalLink, Play, ShieldCheck, Scale } from "lucide-react";

const SCENARIOS = [
    {
        key: "gdpr",
        icon: ShieldCheck,
        label: "GDPR question",
        question: "Can we rely on legitimate interest to send B2B marketing emails in the EU?",
        answer: [
            "Partially, but two regimes apply in parallel, and the stricter one governs the channel.",
            "Under the GDPR, processing business contacts' personal data for direct marketing can rest on legitimate interest. Article 6(1)(f) permits processing necessary for a controller's legitimate interests, and Recital 47 expressly notes that direct marketing 'may be regarded as carried out for a legitimate interest'. You must still run and document a three-part balancing test: purpose, necessity, and the contact's reasonable expectations.",
            "The email channel itself, however, is governed by the ePrivacy Directive. Article 13(1) requires prior opt-in consent for unsolicited electronic mail; the Article 13(2) 'soft opt-in' exception applies only where you obtained the address in the context of a sale of a similar product or service, and every message offers a free, simple opt-out. Several Member States (for example Germany via §7 UWG) apply these rules strictly even in B2B contexts.",
            "Practical position: legitimate interest can carry the underlying data processing, but the sending of the email usually needs consent or a valid soft opt-in, and Article 21(2) GDPR gives recipients an absolute right to object to direct marketing at any time.",
        ],
        sources: [
            { title: "GDPR, Article 6(1)(f), lawfulness of processing", cite: "Regulation (EU) 2016/679", url: "https://eur-lex.europa.eu/eli/reg/2016/679/oj" },
            { title: "GDPR, Recital 47, direct marketing as legitimate interest", cite: "Regulation (EU) 2016/679", url: "https://eur-lex.europa.eu/eli/reg/2016/679/oj" },
            { title: "ePrivacy Directive, Article 13, unsolicited communications", cite: "Directive 2002/58/EC", url: "https://eur-lex.europa.eu/eli/dir/2002/58/oj" },
            { title: "GDPR, Article 21(2), right to object to direct marketing", cite: "Regulation (EU) 2016/679", url: "https://eur-lex.europa.eu/eli/reg/2016/679/oj" },
        ],
    },
    {
        key: "caselaw",
        icon: Scale,
        label: "Case-law lookup",
        question: "What did the CJEU decide in Schrems II about Standard Contractual Clauses?",
        answer: [
            "In Case C-311/18 (Facebook Ireland and Schrems, 16 July 2020), the Court of Justice reached two landmark conclusions.",
            "First, it invalidated the EU-US Privacy Shield adequacy decision (Decision 2016/1250). US surveillance programmes under Section 702 FISA and Executive Order 12333 were held not to be limited to what is strictly necessary, and data subjects lacked actionable judicial redress, falling short of the 'essential equivalence' standard required by Article 45 GDPR read with Articles 7, 8 and 47 of the Charter.",
            "Second, it upheld the validity of Standard Contractual Clauses (Decision 2010/87), but conditionally. Exporters and importers must verify, prior to any transfer, whether the law of the destination country allows the importer to actually comply with the clauses. Where it does not, the parties must adopt supplementary measures; if effective protection still cannot be ensured, the exporter, or failing that the supervisory authority, must suspend the transfer.",
            "The ruling reshaped EU transfer practice: it drove the 2021 modernised SCCs (Decision 2021/914), the EDPB's supplementary-measures recommendations, and ultimately the EU-US Data Privacy Framework adequacy decision of 10 July 2023, itself now under challenge before the General Court.",
        ],
        sources: [
            { title: "CJEU, Case C-311/18, Facebook Ireland Ltd v Maximillian Schrems", cite: "ECLI:EU:C:2020:559, 16 July 2020", url: "https://curia.europa.eu/juris/liste.jsf?num=C-311/18" },
            { title: "GDPR, Chapter V, transfers to third countries (Arts. 44-49)", cite: "Regulation (EU) 2016/679", url: "https://eur-lex.europa.eu/eli/reg/2016/679/oj" },
            { title: "Commission Implementing Decision (EU) 2021/914, modernised SCCs", cite: "OJ L 199, 7.6.2021", url: "https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj" },
            { title: "EU-US Data Privacy Framework adequacy decision", cite: "C(2023) 4745, 10 July 2023", url: "https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=uriserv%3AOJ.L_.2023.231.01.0118.01.ENG" },
        ],
    },
    {
        key: "compliance",
        icon: CheckCircle2,
        label: "Compliance assessment",
        question: "We deploy an AI tool that screens job applicants' CVs. What are our EU AI Act obligations?",
        answer: [
            "Your system is high-risk. Annex III, point 4(a) of the AI Act covers AI systems intended for the recruitment or selection of natural persons, including screening and filtering applications.",
            "As a deployer, Article 26 requires you to: use the system in accordance with the provider's instructions; assign human oversight to trained, competent staff; ensure input data is relevant and sufficiently representative; monitor operation and suspend use if you suspect risk; keep automatically generated logs for at least six months; and inform affected workers and their representatives before putting the system into service. Because recruitment decisions affect natural persons, Article 26(7) and Article 86 add information and explanation duties towards candidates, alongside GDPR Article 22 constraints on solely automated decisions.",
            "If you were to substantially modify the tool or market it under your own name, you would assume full provider obligations (Articles 8-15: risk management, data governance, technical documentation, accuracy and cybersecurity) via Article 25.",
            "Timeline: the AI Act entered into force on 1 August 2024; prohibitions and AI-literacy duties have applied since 2 February 2025 and GPAI/governance rules since 2 August 2025. The main body applies from 2 August 2026, but under the Digital Omnibus of 7 May 2026 (approved by Parliament, pending final formal adoption) the Annex III high-risk obligations, including recruitment systems, are deferred to 2 December 2027. Use the interim period to build oversight, logging and candidate-information processes.",
        ],
        sources: [
            { title: "AI Act, Annex III, point 4(a): employment and worker management", cite: "Regulation (EU) 2024/1689", url: "https://eur-lex.europa.eu/eli/reg/2024/1689/oj" },
            { title: "AI Act, Article 26, obligations of deployers of high-risk AI", cite: "Regulation (EU) 2024/1689", url: "https://eur-lex.europa.eu/eli/reg/2024/1689/oj" },
            { title: "AI Act, Article 25, responsibilities along the value chain", cite: "Regulation (EU) 2024/1689", url: "https://eur-lex.europa.eu/eli/reg/2024/1689/oj" },
            { title: "Digital Omnibus amendments, high-risk timeline deferral (pending adoption)", cite: "COM(2025) proposal, provisional agreement 7 May 2026", url: "https://digital-strategy.ec.europa.eu/en/policies/regulatory-framework-ai" },
        ],
    },
];

const TR_SCENARIOS = [
    {
        key: "kvkk",
        icon: ShieldCheck,
        label: "KVKK ve GDPR karşılaştırması",
        question: "Hem Türkiye'de hem AB'de faaliyet gösteriyoruz: KVKK ve GDPR kapsamında açık rıza gereklilikleri nasıl farklılaşıyor?",
        answer: [
            "İki rejim benzer bir temelden hareket eder, ancak rızanın sistemdeki rolü önemli ölçüde farklıdır.",
            "Türk hukukunda kişisel verilerin işlenmesi, 6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK, RG 07.04.2016, No. 29677) ile düzenlenir. Madde 5/1 uyarınca ana kural, ilgili kişinin açık rızasıdır; madde 3'te açık rıza, belirli bir konuya ilişkin, bilgilendirilmeye dayanan ve özgür iradeyle açıklanan rıza olarak tanımlanır. Madde 5/2'deki istisnalar (kanunlarda öngörülme, sözleşmenin kurulması veya ifası, hukuki yükümlülük, meşru menfaat ve diğerleri) ancak rızanın alternatifi olarak uygulanır.",
            "GDPR'de (Tüzük (AB) 2016/679) ise rıza, 6(1) maddesindeki altı eşdeğer hukuki dayanaktan yalnızca biridir; meşru menfaat (6(1)(f)) günlük işleme faaliyetlerinde yaygın bir dayanaktır. Rızanın koşulları 4(11) ve 7. maddelerde düzenlenir: özgürce verilmiş, belirli, bilgilendirilmiş ve açık bir irade beyanı olmalı, her an geri alınabilmelidir.",
            "Özel nitelikli (hassas) verilerde fark 2024'te daralmıştır: 7499 sayılı Kanun (RG 12.03.2024, No. 32487) ile KVKK madde 6 değiştirilmiş ve GDPR madde 9'a yaklaşan istisnalar getirilmiştir. Yine de Kurul kararları ile Avrupa Veri Koruma Kurulu rehberleri arasında yorum farkları sürmektedir; iki rejimde ayrı ayrı uyum doğrulaması gerekir.",
            "Bu içerik hukuki bilgidir, hukuki tavsiye değildir.",
        ],
        sources: [
            { title: "KVKK, 6698 sayılı Kanun, m. 3, 5 ve 6", cite: "RG 07.04.2016, No. 29677", url: "https://www.mevzuat.gov.tr/mevzuat?MevzuatNo=6698&MevzuatTur=1&MevzuatTertip=5" },
            { title: "7499 sayılı Kanun ile KVKK değişiklikleri", cite: "RG 12.03.2024, No. 32487", url: "https://www.resmigazete.gov.tr/eskiler/2024/03/20240312-1.htm" },
            { title: "GDPR, Madde 4(11), 6(1) ve 7: rızanın koşulları", cite: "Tüzük (AB) 2016/679", url: "https://eur-lex.europa.eu/eli/reg/2016/679/oj" },
            { title: "KVKK Kurumu, Açık Rıza Rehberi", cite: "kvkk.gov.tr", url: "https://www.kvkk.gov.tr/Icerik/2037/Acik-Riza" },
        ],
    },
];

const TR_UI = {
    eyebrow: "Uygulamada görün",
    heading: "Gerçek bir araştırma yanıtının oluşumunu izleyin.",
    blurb: "Platformdan önceden kaydedilmiş bir senaryo. Her ifade, doğrulanabilir resmi kaynaklara dayanır. Senaryoyu seçin ve yanıtın akışını izleyin.",
    panel: "Araştırma Asistanı: kayıtlı demo",
    skip: "Animasyonu atla",
    choose: "Tam atıflı gerçek bir yanıtı izlemek için soldaki senaryoyu seçin; hesap gerekmez.",
    question: "Soru",
    sources: "Doğrulanmış kaynaklar",
    cta: "Ücretsiz denemeyi başlatın",
    ctaNote: "7 gün ücretsiz · kredi kartı gerekmez · tam erişim",
};

export default function DemoShowcase({ lang = "en" }) {
    const isTr = lang === "tr";
    const LIST = isTr ? TR_SCENARIOS : SCENARIOS;
    const [active, setActive] = useState(null);
    const [shown, setShown] = useState("");
    const [phase, setPhase] = useState("idle"); // idle | streaming | done
    const timerRef = useRef(null);

    const scenario = LIST.find((s) => s.key === active);
    const fullText = scenario ? scenario.answer.join("\n\n") : "";

    useEffect(() => () => clearInterval(timerRef.current), []);

    function play(key) {
        clearInterval(timerRef.current);
        setActive(key);
        setShown("");
        setPhase("streaming");
        const text = LIST.find((s) => s.key === key).answer.join("\n\n");
        let i = 0;
        timerRef.current = setInterval(() => {
            i = Math.min(text.length, i + 4);
            setShown(text.slice(0, i));
            if (i >= text.length) {
                clearInterval(timerRef.current);
                setPhase("done");
            }
        }, 14);
    }

    function skip() {
        clearInterval(timerRef.current);
        setShown(fullText);
        setPhase("done");
    }

    return (
        <section id="demo" className="bg-[#0D1B3E] text-[#F5F0E8] py-20 lg:py-28" data-testid="demo-section">
            <div className="container-edit">
                <div className="grid gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                            {isTr ? TR_UI.eyebrow : "See it in action"}
                        </div>
                        <h2 className="font-display text-3xl lg:text-5xl mt-3 tracking-tight leading-[1.08]">
                            {isTr ? TR_UI.heading : "Watch a real research answer take shape."}
                        </h2>
                        <p className="mt-5 text-[#F5F0E8]/70">
                            {isTr
                                ? TR_UI.blurb
                                : "Three pre-recorded scenarios from the platform. Every statement grounded in verifiable EU sources. Pick one and watch the answer stream in, exactly as it does for subscribers."}
                        </p>
                        <div className="mt-8 space-y-3">
                            {LIST.map((s) => {
                                const Icon = s.icon;
                                const isActive = active === s.key;
                                return (
                                    <button
                                        key={s.key}
                                        type="button"
                                        onClick={() => play(s.key)}
                                        data-testid={`demo-scenario-${s.key}`}
                                        className={`w-full text-left p-4 border transition-colors flex items-start gap-3 ${
                                            isActive
                                                ? "border-[#C9A84C] bg-[#C9A84C]/10"
                                                : "border-[#F5F0E8]/20 hover:border-[#C9A84C]/60"
                                        }`}
                                    >
                                        <Icon size={18} color="#C9A84C" className="mt-0.5 shrink-0" />
                                        <span>
                                            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#F5F0E8]/50">
                                                {s.label}
                                            </span>
                                            <span className="block mt-1 text-sm text-[#F5F0E8]/90 leading-snug">
                                                {s.question}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        <div className="border border-[#F5F0E8]/15 bg-[#0A1530] min-h-[420px] flex flex-col" data-testid="demo-panel">
                            <div className="px-5 py-3 border-b border-[#F5F0E8]/10 flex items-center justify-between gap-3">
                                <span className="text-[10px] uppercase tracking-[0.24em] text-[#F5F0E8]/50">
                                    {isTr ? TR_UI.panel : "Research Assistant: recorded demo"}
                                </span>
                                {phase === "streaming" && (
                                    <button
                                        type="button"
                                        onClick={skip}
                                        className="text-[10px] uppercase tracking-[0.2em] text-[#C9A84C] hover:opacity-80"
                                        data-testid="demo-skip"
                                    >
                                        {isTr ? TR_UI.skip : "Skip animation"}
                                    </button>
                                )}
                            </div>

                            {!scenario ? (
                                <div className="flex-1 grid place-items-center p-10 text-center">
                                    <div>
                                        <Play size={28} color="#C9A84C" className="mx-auto" />
                                        <p className="mt-4 text-[#F5F0E8]/60 max-w-sm">
                                            {isTr
                                                ? TR_UI.choose
                                                : "Choose a scenario on the left to replay a real, fully-cited answer, no account needed."}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-6 lg:p-8 flex-1">
                                    <div className="text-sm text-[#C9A84C] uppercase tracking-[0.2em] text-[11px]">
                                        {isTr ? TR_UI.question : "Question"}
                                    </div>
                                    <p className="mt-2 font-display text-lg lg:text-xl leading-snug">
                                        {scenario.question}
                                    </p>
                                    <hr className="my-5 border-[#F5F0E8]/10" />
                                    <div
                                        className="text-[15px] leading-relaxed text-[#F5F0E8]/85 whitespace-pre-line"
                                        data-testid="demo-answer"
                                    >
                                        {shown}
                                        {phase === "streaming" && (
                                            <span className="inline-block w-2 h-4 bg-[#C9A84C] ml-0.5 animate-pulse align-middle" />
                                        )}
                                    </div>

                                    {phase === "done" && (
                                        <>
                                            <div className="mt-8 border border-[#C9A84C]/40 bg-[#C9A84C]/5 p-5 animate-fade-up" data-testid="demo-sources">
                                                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#C9A84C]">
                                                    <BookOpen size={13} /> {isTr ? TR_UI.sources : "Verified sources"} ({scenario.sources.length})
                                                </div>
                                                <ul className="mt-3 space-y-2.5">
                                                    {scenario.sources.map((src, i) => (
                                                        <li key={i} className="text-sm">
                                                            <a
                                                                href={src.url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="inline-flex items-start gap-2 hover:text-[#C9A84C] transition-colors"
                                                            >
                                                                <ExternalLink size={13} className="mt-1 shrink-0" color="#C9A84C" />
                                                                <span>
                                                                    {src.title}
                                                                    <span className="block text-xs text-[#F5F0E8]/50">{src.cite}</span>
                                                                </span>
                                                            </a>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="mt-8 flex flex-wrap items-center gap-4 animate-fade-up" data-testid="demo-cta">
                                                <Link to="/signup" className="btn-primary" data-testid="demo-cta-trial">
                                                    {isTr ? TR_UI.cta : "Start your free trial"} <ArrowRight size={15} />
                                                </Link>
                                                <span className="text-xs text-[#F5F0E8]/55">
                                                    {isTr ? TR_UI.ctaNote : "7 days free · no credit card · full platform access"}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
